/**
 *
 * popover.js
 * Provides positioning and accessibile relationship functionality to BUX
 * Context Menu and BUX Tooltip components via parent classes.
 *
 *
 * Popover positioning -- see Popovers Playground in demo section
 * First, specify the container's positioning relative to the trigger
 *   container--position-above-trigger
 *   container--position-below-trigger
 *   container--position-left-of-trigger
 *   container--position-right-of-trigger
 *
 * Then, specify how the caret should align or justify to the trigger
 * For container--position-above-trigger and container--position-below-trigger:
 *   caret--justify-trigger-left
 *   caret--justify-trigger-center
 *   caret--justify-trigger-right
 * For container--position-left-of-trigger and container--position-right-of-trigger:
 *   caret--align-trigger-start
 *   caret--align-trigger-center
 *   caret--align-trigger-end
 *
 * Finally, specify how the container should align or justify to the caret
 * For container--position-above-trigger and container--position-below-trigger:
 *   container--justify-caret-left
 *   container--justify-caret-center
 *   container--justify-caret-right
 * For container--position-left-of-trigger and container--position-right-of-trigger:
 *   container--align-caret-start
 *   container--align-caret-center
 *   container--align-caret-end
 *
 */

/**
 * If classnames or ID name conventions change, they can be adjusted once here rather than require digging through the
 * component logic looking for query selectors and whatnot.
 *
 * This is deliberately exposed to the global namespace to allow other components to access it
 */
const $popoverConfig = {
  classNames: {
    container: {
      root: 'bux-popover__container',
      expanded: 'container--expanded',
      collapsed: 'visually-hidden',
      /**
       * The class that indicates this is a context menu container (as opposed to a tooltip)
       */
      contextMenu: 'bux-context-menu__container',
      /**
       * How the container is positioned relative to the trigger
       */
      position: {
        above: 'container--position-above-trigger',
        below: 'container--position-below-trigger',
        left: 'container--position-left-of-trigger',
        right: 'container--position-right-of-trigger',
      },
      /**
       * How the container is positioned relative to the caret
       */
      caret: {
        /**
         * When the container is left/right of the trigger
         */
        align: {
          start: 'container--align-caret-start',
          center: 'container--align-caret-center',
          end: 'container--align-caret-end',
        },
        /**
         * When the container is above/below the trigger
         */
        justify: {
          left: 'container--justify-caret-left',
          center: 'container--justify-caret-center',
          right: 'container--justify-caret-right',
        }
      }
    },
    trigger: {
      root: 'bux-popover__trigger',
      toggleTypes: {
        click: 'trigger--click',
        hover: 'trigger--hover',
      }
    },
    caret: {
      /**
       * When the container is above/below the trigger, how the caret is justified relative to the trigger
       */
      justify: {
        triggerLeft: 'caret--justify-trigger-left',
        triggerCenter: 'caret--justify-trigger-center',
        triggerRight: 'caret--justify-trigger-right',
      },
      /**
       * When the container is to the left/right of the trigger, how the caret is aligned relative to the trigger
       */
      align: {
        triggerStart: 'caret--align-trigger-start',
        triggerCenter: 'caret--align-trigger-center',
        triggerEnd: 'caret--align-trigger-end',
      }
    }
  },
  queries: {
    caret: '.bux-popover__caret',
  },
  ids: {
    container: (uid) => `bux-popover__container--${ uid }`,
  },
};


/* =========
    Helpers
   ========= */

/**
 * Given an object with classnames keyed by a string, returns the string key name of the first class found
 * in the provided class list.
 */
const getKeyFromClass = (classesByKey,
  classList) => Object.entries(classesByKey)
  .find(([_classKey, className]) => classList.contains(className))?.[0];

/**
 * Generates and returns a unique ID for the context menu container so the trigger can set up a DOM relationship with it
 */
const getContainerDomId = () => {
  const uid = Math.random().toString(36).slice(2, 8);
  return $popoverConfig.ids.container(uid);
};


/* ==============
    Main classes
   ============== */

/**
 * Handles a context menu that handles expand/collapse functionality and instantiates the items it contains with
 * `BuxPopoverItem`s.
 */
export class BuxPopoverController {
  constructor(triggerElement, containerElement) {
    this.trigger = new BuxPopoverTrigger(triggerElement, this);
    this.container = new BuxPopoverContainer(containerElement, this);

    const containerDomId = getContainerDomId();
    this.container.element.setAttribute('id', containerDomId);
    /* Context Menu-specific relationships */
    if (this.isContextMenu) {
      this.trigger.element.setAttribute('aria-controls', containerDomId);
      /* Persistent listener for toggling */
      this.trigger.element.addEventListener('click', this.toggle.bind(this));
    } else {
      this.trigger.element.querySelector('[role="img"], .icon, img, svg')?.setAttribute('aria-describedby', containerDomId);
    }

    /* Listener to allow other scripts to tell popovers to update their position */
    window.addEventListener('bux:popover:update-position',
      this.container.updatePositioning.bind(this.container));

    /* Initialize AbortController for window click listener */
    this.abortController = new AbortController();
  }

  /**
   * Whether this is an instance of a BUX Context Menu (as opposed to a BUX Tooltip)
   */
  get isContextMenu() {
    return this.container.element.classList.contains($popoverConfig.classNames.container.contextMenu);
  }

  /**
   * Whether the context menu or tooltip is expanded or collapsed. Derived from the trigger's `data-is-expanded`
   * attribute, and sync'd via the `isExpanded` setter.
   */
  get isExpanded() {
    return this.trigger.element.getAttribute('data-is-expanded') === 'true';
  }

  /**
   * Convenient place for state change effects since we derive the expanded/collapsed state from the trigger's
   * `data-is-expanded`.
   */
  set isExpanded(value) {
    if (value) {
      this.container.expand();
      this.trigger.expand();

      this.abortController = new AbortController();
      window.addEventListener("keydown",
        this.maybeCollapseFromKeydown.bind(this),
        {
          signal: this.abortController.signal,
          passive: true
        });
      window.addEventListener('click',
        this.handleWindowClick.bind(this),
        {
          signal: this.abortController.signal,
          passive: true
        });
      window.addEventListener('scroll',
        this.container.updatePositioning.bind(this.container),
        {
          signal: this.abortController.signal,
          passive: true
        });
      window.addEventListener('resize',
        this.container.updatePositioning.bind(this.container),
        {
          signal: this.abortController.signal,
          passive: true
        });
    } else {
      this.container.collapse();
      this.trigger.collapse();
      this.abortController.abort();
    }
  }

  expand() {
    if (this.isExpanded) return;
    this.isExpanded = true;
  }

  collapse() {
    if (!this.isExpanded) return;
    this.isExpanded = false;
  }

  toggle() {
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Collapse the menu if the user clicks outside of it.
   */
  handleWindowClick(event) {
    /* data-stay-open="true" is used in the playground demo to keep the popover open while clicking radio buttons */
    if (this.trigger.element.dataset.stayOpen === 'true') return;

    const domPath = event.composedPath();
    if (domPath.includes(this.container.element) ||
      domPath.includes(this.trigger.element)) {
      return;
    }
    this.collapse();
  }

  maybeCollapseFromKeydown(event) {
    if (event.key === "Escape") {
      this.collapse()
    }
  }
}

/**
 * Manages popover container positioning and expand/collapse behavior
 */
class BuxPopoverContainer {
  constructor(containerElement, parentController) {
    this.element = containerElement;
    this.parentController = parentController;
    this.trigger = this.parentController.trigger;

    this.element.style.display = "none";
    this.element.classList.add($popoverConfig.classNames.container.collapsed);
    this.setDefaultPositioning();

    const caretElement = this.element.querySelector($popoverConfig.queries.caret);
    this.caret = new BuxPopoverCaret(caretElement, this);
  }

  get positionFromClass() {
    return getKeyFromClass($popoverConfig.classNames.container.position,
      this.element.classList);
  }

  get position() {
    return this.positionFromClass;
  }

  get positionIsHorizontal() {
    return ['left', 'right'].includes(this.positionFromClass);
  }

  get positionIsVertical() {
    return ['above', 'below'].includes(this.positionFromClass);
  }

  get distanceToTrigger() {
    if (this.positionIsVertical) {
      return this.getTransformForVertical(this.trigger.rect, this.caret.rect);
    } else if (this.positionIsHorizontal) {
      return this.getTransformForHorizontal(this.trigger.rect, this.caret.rect);
    }
  }

  get activeTransform() {
    return {
      x: +(window.getComputedStyle(this.element)
        .getPropertyValue('--transform-x')
        .replace('px', '')),
      y: +(window.getComputedStyle(this.element)
        .getPropertyValue('--transform-y')
        .replace('px', '')),
    };
  }

  /**
   * Set defaults for positioning if not specified
   */
  setDefaultPositioning() {
    if (!this.positionFromClass) {
      this.element.classList.add($popoverConfig.classNames.container.position.below);
      this.element.classList.add($popoverConfig.classNames.container.caret.justify.left);
    }
  }

  expand() {
    this.element.style.removeProperty("display");
    this.element.classList.remove($popoverConfig.classNames.container.collapsed);
    requestAnimationFrame(() => {
      this.element.classList.add($popoverConfig.classNames.container.expanded);
      this.updatePositioning();
    });

  }

  collapse() {
    this.element.classList.remove($popoverConfig.classNames.container.expanded);

    if (this.trigger.element.contains(document.activeElement) ||
      this.parentController.container.element.contains(document.activeElement)) {
      this.trigger.element.focus();
    }

    this.element.addEventListener('transitionend', () => {
      if (!this.parentController.isExpanded) {
        this.element.style.removeProperty('--transform-x');
        this.element.style.removeProperty('--transform-y');
        this.element.style.display = "none";
        this.element.classList.add($popoverConfig.classNames.container.collapsed);
      }
    }, { once: true });

  }

  updatePositioning() {
    const { x,  y } = this.distanceToTrigger;
    if (x !== 0) {
      this.element.style.setProperty('--transform-x', `${ x }px`);
    }
    if (y !== 0) {
      this.element.style.setProperty('--transform-y', `${ y }px`);
    }
  }

  getTransformForHorizontal(triggerRect, caretRect) {
    let x, y;

    if (this.position === 'left') {
      x = triggerRect.left - caretRect.right;
    } else if (this.position === 'right') {
      x = triggerRect.right - caretRect.left;
    }

    if (this.caret.align === 'triggerStart') {
      y = triggerRect.top - caretRect.top + (caretRect.height / 2);
    } else if (this.caret.align === 'triggerCenter') {
      y =
        (triggerRect.top + (triggerRect.height / 2)) -
        (caretRect.top + (caretRect.height / 2));
    } else if (this.caret.align === 'triggerEnd') {
      y = triggerRect.bottom - caretRect.bottom;
    }

    return {
      x: x + this.activeTransform.x,
      y: y + this.activeTransform.y
    };
  }

  getTransformForVertical(triggerRect, caretRect) {
    let x, y;
    if (this.position === 'above') {
      y = triggerRect.top - caretRect.bottom;
    } else if (this.position === 'below') {
      y = triggerRect.bottom - caretRect.top;
    }
    if (this.caret.justify === 'triggerLeft') {
      x = triggerRect.left - caretRect.left;
    } else if (this.caret.justify === 'triggerCenter') {
      x =
        (triggerRect.left + (triggerRect.width / 2)) -
        (caretRect.left + (caretRect.width / 2));
    } else if (this.caret.justify === 'triggerRight') {
      x = triggerRect.right - caretRect.right;
    }
    return {
      x: x + this.activeTransform.x,
      y: y + this.activeTransform.y
    };
  }
}

/**
 * Handles default positioning
 */
class BuxPopoverCaret {
  constructor(caretElement, parentContainer) {
    this.element = caretElement;
    this.parentContainer = parentContainer;

    this.setDefaultPositioning();
  }

  get rect() {
    return this.element.getBoundingClientRect();
  }

  /**
   * Gets the caret's alignment key -- meaning, how it is vertically aligned to
   * the trigger when the container is positioned to the left or right -- by
   * finding the first class name in its classList that matches a class name
   * from the configuration object.
   * @returns {'triggerStart' | 'triggerCenter' | 'triggerEnd'}
   */
  get alignFromClass() {
    return getKeyFromClass($popoverConfig.classNames.caret.align,
      this.element.classList);
  }

  /**
   * Gets the caret's justification key -- meaning, how it is horizontally
   * justified on the trigger element when the container is positioned above or
   * below the trigger -- by finding the first class name in its classList that
   * matches a class name from the configuration object.
   * @returns {'triggerStart' | 'triggerCenter' | 'triggerEnd'}
   */
  get justifyFromClass() {
    return getKeyFromClass($popoverConfig.classNames.caret.justify,
      this.element.classList);
  }

  /**
   * Returns the caret's alignment key, falling back to "triggerCenter" if no
   * class name is present on the caret that specifies its alignment.
   * @returns {'triggerStart'|'triggerCenter'|'triggerEnd'|string}
   */
  get align() {
    return this.alignFromClass ?? 'triggerCenter';
  }

  /**
   * Returns the caret's alignment key, falling back to "triggerCenter" if no
   * class name is present on the caret that specifies its justification.
   * @returns {'triggerStart'|'triggerCenter'|'triggerEnd'|string}
   */
  get justify() {
    return this.justifyFromClass ?? 'triggerCenter';
  }

  /**
   * Adds the default `triggerCenter` class name for either alignment or
   * justification to the caret element. Reads the container positioning to see
   * which -- align or justify -- is needed.
   */
  setDefaultPositioning() {
    if (this.parentContainer.positionIsVertical) {
      if (!this.justifyFromClass) {
        this.element.classList.add($popoverConfig.classNames.caret.justify.triggerCenter);
      }
    } else if (this.parentContainer.positionIsHorizontal) {
      if (!this.alignFromClass) {
        this.element.classList.add($popoverConfig.classNames.caret.align.triggerCenter);
      }
    }
  }

}

/**
 * Just handles its toggle behavior, mostly
 * Also makes sure the trigger is focusable
 */
class BuxPopoverTrigger {
  constructor(triggerElement, parentController) {
    this.parentController = parentController;
    this.element = triggerElement;
    if (typeof this.element.getAttribute('tabindex') !== "number") {
      this.element.setAttribute('tabindex', "0")
    }
  }

  get rect() {
    return this.element.getBoundingClientRect();
  }

  expand() {
    if (this.parentController.isContextMenu) {
      this.element.setAttribute('aria-expanded', 'true');
    }
    this.element.setAttribute('data-is-expanded', 'true');
  }

  collapse() {
    if (this.parentController.isContextMenu) {
      this.element.setAttribute('aria-expanded', 'false');
    }
    this.element.setAttribute('data-is-expanded', 'false');
  }

}
